import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  type BrokerPartner,
  type BrokerPartnerInput,
  type BrokerPartnerRole,
} from '@/shared/api/resources';
import {
  formatCpfCnpj,
  formatRate,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

import { papeisDoSocio } from './rotulos';

/** Os tres blocos da aba Socios do OnPoint, na ordem dela. */
const blocos: { papel: BrokerPartnerRole; titulo: string; vazio: string }[] = [
  { papel: 'socio', titulo: 'Socios', vazio: 'Nenhum socio cadastrado' },
  {
    papel: 'administrador',
    titulo: 'Administradores',
    vazio: 'Nenhum administrador cadastrado',
  },
  {
    papel: 'representante',
    titulo: 'Representantes',
    vazio: 'Nenhum representante cadastrado',
  },
];

/**
 * Aba Socios do corretor (e "Socios / Administradores PEP" da corretora): os blocos
 * Socios, Administradores e Representantes, com nome, CPF ou CNPJ, participacao e
 * PEP.
 *
 * O PEP e marcado por quem cadastra. O efeito do PEP do corretor (esteira, aprovacao)
 * e regra pendente: a marca so registra.
 */
export function BrokerPartnersSection({
  corretoraId,
  podeEditar,
}: {
  corretoraId: string;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<{
    papel: BrokerPartnerRole;
    socio?: BrokerPartner;
  } | null>(null);
  const [removendo, setRemovendo] = useState<BrokerPartner | null>(null);

  const consulta = useQuery({
    queryKey: ['broker', corretoraId, 'partners'],
    queryFn: ({ signal }) => brokers.partners(corretoraId, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['broker', corretoraId] });
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'broker', corretoraId],
    });
  }

  const remover = useMutation({
    mutationFn: (id: string) => brokers.removePartner(corretoraId, id),
    onSuccess: () => {
      toast.success('Pessoa excluida do quadro.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const socios = consulta.data ?? [];
  const nomes = new Map(socios.map((socio) => [socio.id, socio.name]));

  return (
    <div className="flex flex-col gap-4">
      {blocos.map((bloco) => {
        const linhas = socios.filter((socio) => socio.role === bloco.papel);
        const colunas: Column<BrokerPartner>[] = [
          {
            key: 'nome',
            header: 'Nome',
            render: (socio) => (
              <div className="min-w-0">
                <p className="truncate font-medium text-ink-900">{socio.name}</p>
                {socio.parent_id ? (
                  <p className="truncate text-2xs text-ink-500">
                    Socio de {nomes.get(socio.parent_id) ?? 'outra pessoa do quadro'}
                  </p>
                ) : null}
              </div>
            ),
          },
          {
            key: 'documento',
            header: 'CPF/CNPJ',
            render: (socio) =>
              socio.document ? (
                <span className="tabular-nums">{formatCpfCnpj(socio.document)}</span>
              ) : (
                <span className="text-ink-400">nao informado</span>
              ),
          },
          ...(bloco.papel === 'socio'
            ? [
                {
                  key: 'participacao',
                  header: 'Participacao',
                  align: 'right' as const,
                  render: (socio: BrokerPartner) =>
                    socio.participation_bps === null
                      ? ''
                      : formatRate(socio.participation_bps),
                },
              ]
            : []),
          {
            key: 'pep',
            header: 'PEP',
            render: (socio) =>
              socio.is_pep ? (
                <Badge tone="warning">Sim</Badge>
              ) : (
                <Badge tone="neutral">Nao</Badge>
              ),
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (socio: BrokerPartner) => (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditando({ papel: socio.role, socio })}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemovendo(socio)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ];
        return (
          <DetailCard
            key={bloco.papel}
            title={bloco.titulo}
            loading={consulta.isPending}
            error={consulta.error}
            action={
              podeEditar ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditando({ papel: bloco.papel })}
                >
                  Adicionar
                </Button>
              ) : undefined
            }
          >
            <DataTable
              columns={colunas}
              items={linhas}
              keyOf={(socio) => socio.id}
              empty={<EmptyState title={bloco.vazio} />}
            />
          </DetailCard>
        );
      })}

      {editando ? (
        <PartnerDialog
          corretoraId={corretoraId}
          papel={editando.papel}
          socio={editando.socio}
          quadro={socios}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            invalidar();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => removendo && remover.mutate(removendo.id)}
        title="Excluir do quadro societario?"
        message={`${removendo?.name ?? ''} sai do quadro, com as pessoas penduradas nela. A exclusao fica no historico do corretor.`}
        confirmLabel="Excluir"
        tone="danger"
        loading={remover.isPending}
      />
    </div>
  );
}

function PartnerDialog({
  corretoraId,
  papel,
  socio,
  quadro,
  onClose,
  onSalvo,
}: {
  corretoraId: string;
  papel: BrokerPartnerRole;
  socio: BrokerPartner | undefined;
  quadro: BrokerPartner[];
  onClose: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const [role, setRole] = useState<BrokerPartnerRole>(socio?.role ?? papel);
  const [nome, setNome] = useState(socio?.name ?? '');
  const [documento, setDocumento] = useState(socio?.document ?? '');
  const [participacao, setParticipacao] = useState(
    socio?.participation_bps != null ? rateToInput(socio.participation_bps) : '',
  );
  const [pep, setPep] = useState(socio?.is_pep ?? false);
  const [majoritario, setMajoritario] = useState(socio?.is_majority ?? false);
  const [naoConferido, setNaoConferido] = useState(socio?.document_invalid ?? false);
  const [pai, setPai] = useState(socio?.parent_id ?? '');

  const erroParticipacao = participacao ? rateInputError(participacao) : undefined;

  const salvar = useMutation({
    mutationFn: (entrada: BrokerPartnerInput) =>
      socio
        ? brokers.updatePartner(corretoraId, socio.id, entrada)
        : brokers.addPartner(corretoraId, entrada),
    onSuccess: () => {
      toast.success(socio ? 'Pessoa alterada.' : 'Pessoa incluida no quadro.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  // So uma pessoa juridica do quadro pode ter socios pendurados.
  const pais = quadro
    .filter((outro) => outro.person_kind === 'pj' && outro.id !== socio?.id)
    .map((outro) => ({ value: outro.id, label: outro.name }));

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (erroParticipacao) return;
    salvar.mutate({
      role,
      name: nome,
      document: documento,
      participation_bps: participacao ? rateToBasisPoints(participacao) : null,
      is_pep: pep,
      is_majority: majoritario,
      document_invalid: naoConferido,
      parent_id: pai || null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={socio ? `Alterar ${socio.name}` : 'Incluir no quadro societario'}
      description="O PEP e marcado por quem cadastra. O efeito do PEP do corretor e regra pendente, e nada e bloqueado."
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Papel"
            options={papeisDoSocio}
            value={role}
            error={fieldErrorOf(erro, 'role')}
            onChange={(evento) => setRole(evento.target.value as BrokerPartnerRole)}
          />
          <Field
            label="Nome"
            required
            value={nome}
            error={fieldErrorOf(erro, 'name')}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <Field
            label="CPF/CNPJ"
            value={documento}
            error={fieldErrorOf(erro, 'document')}
            hint="O tipo segue a quantidade de digitos."
            onChange={(evento) => setDocumento(evento.target.value)}
          />
          <Field
            label="Participacao (%)"
            inputMode="decimal"
            value={participacao}
            error={erroParticipacao ?? fieldErrorOf(erro, 'participation_bps')}
            onChange={(evento) => setParticipacao(evento.target.value)}
          />
          {pais.length > 0 ? (
            <Select
              label="Socio de"
              placeholder="Direto na corretora"
              options={pais}
              value={pai}
              error={fieldErrorOf(erro, 'parent_id')}
              onChange={(evento) => setPai(evento.target.value)}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-ink-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pep}
              onChange={(evento) => setPep(evento.target.checked)}
            />
            PEP
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={majoritario}
              onChange={(evento) => setMajoritario(evento.target.checked)}
            />
            Socio majoritario
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={naoConferido}
              onChange={(evento) => setNaoConferido(evento.target.checked)}
            />
            Documento nao conferido
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={salvar.isPending}
            disabled={erroParticipacao !== undefined}
          >
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
